// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/MinistryBeforeIndustry.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiMusic,
  FiMic,
  FiHeadphones,
  FiFilm,
  FiPenTool,
  FiBookOpen,
} from "react-icons/fi";
import Button from "../../components/Common/Button";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.homeText};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
  font-size: ${(props) => props.theme.fontSizes.lg};
  text-align: center;
`;

const ProjectsGrid = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.xl};
  margin-top: ${(props) => props.theme.spacing["2xl"]};
`;

const ProjectCard = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    transform: translateX(5px);
    box-shadow: ${(props) => props.theme.shadows.lg};
  }
`;

const ProjectTitle = styled.h4`
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};

  svg {
    color: ${(props) => props.theme.colors.accent};
  }
`;

const ProjectLink = styled(Link)`
  display: inline-block;
  margin-top: 16px;
  color: ${p => p.theme.colors.accent};
  font-weight: 600;
  &:hover { color: ${p => p.theme.colors.homeGold}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.homeGold}; outline-offset: 4px; }
`;

const ProjectDescription = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  font-size: ${(props) => props.theme.fontSizes.sm};
`;



const PrincipleBox = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["2xl"]};
  border-radius: 12px;
  text-align: center;
  margin: ${(props) => props.theme.spacing["3xl"]} 0;
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const ServicesList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    padding: ${(props) => props.theme.spacing.sm} 0;
    color: ${(props) => props.theme.colors.homeText};
    display: flex;
    align-items: center;
    gap: ${(props) => props.theme.spacing.sm};

    &::before {
      content: "🎵";
      font-size: ${(props) => props.theme.fontSizes.sm};
    }
  }
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const MinistryBeforeIndustry = () => {
  const currentProjects = [
    {
      icon: <FiMusic />,
      title: "Project 24: The Return",
      path: "/ministries/ministry-before-industry/project-24",
      description:
        "A 24-song album all about the return of the King, filled with messages of hope and triumph.",
    },
    {
      icon: <FiMic />,
      title: "Selah Songs",
      description:
        "A collection born from spontaneous worship moments, capturing raw, unfiltered worship.",
    },
    {
      icon: <FiBookOpen />,
      title: "The Beatitudes",
      description:
        "Nine songs, nine blessings, one sermon of Jesus - transforming His words into melodies.",
    },
    {
      icon: <FiHeadphones />,
      title: "Mom's Favorite Hymns",
      description:
        "Modern instrumental arrangements of classic high church hymns with tempered variations.",
    },
    {
      icon: <FiFilm />,
      title: "Prophetic Meditations",
      description:
        "Cinematic instrumental tones designed to create atmospheric worship experiences.",
    },
    {
      icon: <FiPenTool />,
      title: "Print & Design Ministry",
      description:
        "Visual designs, slogans, and merchandising that spread our message of faith.",
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Ministry Before Industry - The Music Church</title>
        <meta
          name="description"
          content="Ministry Before Industry (MBI) - Our media production department where artistry bows to anointing, creating content that puts ministry first."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              Ministry Before Industry
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              Where artistry bows to anointing
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/home-preview/mbi-framed-v8.png"
          alt="Ministry Before Industry - Media production department"
          maxWidth="800px"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <nav aria-label="Ministry Before Industry sections" style={{ marginBottom: "32px" }}>
            <ProjectLink to="/books">Books →</ProjectLink>
          </nav>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About MBI</SectionTitle>
                <Text>
                  MBI is our media production department where artistry bows to
                  anointing, and every project is a testament to the truth that
                  ministry leads the way in all we create, and in our efforts,
                  motivations and expectations.
                </Text>
                <Text>
                  Unlike the commercial music industry that often prioritizes
                  profit over purpose, we measure success by lives touched,
                  hearts changed, and souls lifted. Every note, every lyric,
                  every design element serves a higher calling.
                </Text>
              </Section>

              <QuoteBox>"Artistry bows to anointing here"</QuoteBox>

              <Section>
                <SectionTitle>Current Productions</SectionTitle>
                <Text>
                  MBI is actively working on a variety of audiovisual recordings
                  and print media, featuring talent from The Music Church and
                  ministry affiliations worldwide:
                </Text>

                <ProjectsGrid>
                  {currentProjects.map((project, index) => (
                    <ProjectCard
                      key={index}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {project.path && <img src="/images/project24/project-24-the-return.png" alt="Project 24: The Return — project cover" loading="lazy" style={{ display: "block", width: "100%", height: "auto", objectFit: "contain", marginBottom: "20px" }} />}
                      <ProjectTitle>
                        {project.icon}
                        {project.title}
                      </ProjectTitle>
                      <ProjectDescription>
                        {project.description}
                      </ProjectDescription>
                      {project.path && <ProjectLink to={project.path}>Explore Project 24 →</ProjectLink>}
                    </ProjectCard>
                  ))}
                </ProjectsGrid>
              </Section>

              <Section as="section" aria-labelledby="mbi-books-title">
                <SectionTitle id="mbi-books-title">Books</SectionTitle>
                <Text>Explore available books and books in development from our community.</Text>
                <ProjectLink to="/books">Explore Books →</ProjectLink>
              </Section>

              <PrincipleBox>
                <h3 style={{ fontSize: "1.75rem", marginBottom: "1rem" }}>
                  Our Guiding Principle
                </h3>
                <p style={{ fontSize: "1.125rem", lineHeight: "1.8" }}>
                  Every project begins with prayer, proceeds with purpose, and
                  ends with praise. We create not for charts or awards, but for
                  the Kingdom.
                </p>
              </PrincipleBox>

              <Section>
                <SectionTitle>Development Process</SectionTitle>
                <Text>Our creative process is unique in the industry:</Text>
                <ul
                  style={{
                    marginLeft: "2rem",
                    color: "#c6d9e2",
                    lineHeight: "2",
                  }}
                >
                  <li>
                    Projects begin with spiritual confirmation, not market
                    research
                  </li>
                  <li>
                    Content is tested by its ability to minister, not monetize
                  </li>
                  <li>Collaborators are chosen by calling, not just talent</li>
                  <li>
                    Success is measured in transformation, not transactions
                  </li>
                  <li>Distribution prioritizes accessibility over profit</li>
                </ul>
              </Section>

              <Section>
                <SectionTitle>Inspired Content</SectionTitle>
                <Text>
                  Beyond our major productions, MBI develops numerous
                  audiovisual projects, books, videos, and designs that
                  highlight God's amazing grace, mercy, and love. Each piece is
                  crafted to inspire, encourage, and draw people closer to Him.
                </Text>
                <Text>
                  We believe that excellence honors God, so we pursue the
                  highest quality in our productions while never compromising
                  the message for mass appeal.
                </Text>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Production Services
                </SectionTitle>
                <ServicesList>
                  <li>Album Production</li>
                  <li>Music Video Creation</li>
                  <li>Podcast Development</li>
                  <li>Book Publishing</li>
                  <li>Graphic Design</li>
                  <li>Merchandising</li>
                  <li>Digital Content</li>
                </ServicesList>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Ministry Standards
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  Every MBI production adheres to these non-negotiable
                  standards:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <li>Biblical accuracy</li>
                  <li>Spiritual integrity</li>
                  <li>Excellence in craft</li>
                  <li>Ministry over money</li>
                  <li>Kingdom impact focus</li>
                </ul>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Partner With Us
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  Are you a creative who shares our vision? We welcome artists,
                  producers, writers, and designers who understand that ministry
                  comes before industry.
                </Text>
                <Button
                  variant="accent"
                  fullWidth
                  style={{ marginTop: "1rem" }}
                  onClick={() => console.log("Partner clicked")}
                >
                  Submit Your Project
                </Button>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Ready to Create with Purpose?
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Join us in producing content that changes lives and advances the
              Kingdom.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="accent"
                size="large"
                onClick={() => console.log("Explore clicked")}
              >
                Explore Our Catalog
              </Button>
              <Button
                variant="outline"
                size="large"
                style={{ borderColor: "white", color: "white" }}
                onClick={() => console.log("Contact clicked")}
              >
                Contact MBI
              </Button>
            </div>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default MinistryBeforeIndustry;
